import { StreamCallbacks } from './Types';
import { streamLog } from './streamLogger';

export class StreamManager {
  private eventSource: EventSource | null = null;
  private callbacks: StreamCallbacks = {};
  private abortController: AbortController | null = null;

  connect(url: string, callbacks: StreamCallbacks) {
    this.callbacks = callbacks;
    this.eventSource = new EventSource(url);

    // Handle all agent_* events from backend
    const eventTypes = [
      'agent_metadata',
      'agent_tool_call',
      'agent_tool_result',
      'agent_ai_content',
      'agent_complete',
      'agent_done',
      'agent_error'
    ];

    eventTypes.forEach(eventType => {
      this.eventSource!.addEventListener(eventType, (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleEvent(eventType, data);
        } catch (error) {
          try { streamLog.error(`Failed to parse ${eventType}:`, error); } catch (_) {}
          this.callbacks.onError?.(new Error(`Failed to parse ${eventType}: ${error}`));
        }
      });
    });

    this.eventSource.onopen = () => {
      try { streamLog.init('🌊 SSE connection opened'); } catch (_) {}
    };

    this.eventSource.onerror = (error) => {
      try { streamLog.error('🚨 SSE connection error:', error); } catch (_) {}
      this.callbacks.onError?.(new Error('Stream connection error'));
    };
  }

  private handleEvent(type: string, data: any) {
    switch (type) {
      case 'agent_metadata':
        this.callbacks.onMetadata?.(data);
        break;
      case 'agent_tool_call':
        this.callbacks.onToolCall?.(data);
        break;
      case 'agent_tool_result':
        this.callbacks.onToolResult?.(data);
        break;
      case 'agent_ai_content':
        this.callbacks.onAIContent?.(data);
        break;
      case 'agent_complete':
        this.callbacks.onComplete?.(data);
        break;
      case 'agent_done':
        this.callbacks.onDone?.();
        this.disconnect();
        break;
      case 'agent_error':
        try {
          const message = typeof data === 'string' ? data : (data?.error || 'Unknown streaming error');
          this.callbacks.onError?.(new Error(message));
        } catch (_e) {
          this.callbacks.onError?.(new Error('Unknown streaming error'));
        }
        break;
    }
  }

  disconnect() {
    if (this.eventSource) {
      try { streamLog.wrap('🔌 Disconnecting SSE stream'); } catch (_) {}
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.abortController) {
      try { streamLog.wrap('🔌 Aborting fetch-based SSE stream'); } catch (_) {}
      this.abortController.abort();
      this.abortController = null;
    }
  }

  isConnected(): boolean {
    const esOpen = this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
    const fetchActive = this.abortController !== null; // Heuristic: active when we hold an abort controller
    return esOpen || fetchActive;
  }

  getReadyState(): number {
    return this.eventSource?.readyState ?? EventSource.CLOSED;
  }

  // Fetch-based SSE connection (POST), parses SSE frames from response body
  async connectViaFetch(url: string, init: RequestInit, callbacks: StreamCallbacks, capture?: any) {
    this.callbacks = callbacks;
    this.abortController = new AbortController();
    const controller = this.abortController;

    try {
      const response = await fetch(url, { ...init, signal: controller.signal });
      try { streamLog.debug('[SSE][fetch] status', response.status, response.ok, response.headers.get('content-type')); } catch(_) {}
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        const errorMsg = `Stream request failed: ${response.status} ${text}`;
        
        if (capture) {
          capture.captureError(errorMsg);
          capture.finalize();
        }
        
        throw new Error(errorMsg);
      }

      // Optionally validate content type starts with text/event-stream
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('text/event-stream')) {
        // Many proxies still pass-through; don't hard fail, but log
        // intentionally suppressed unless needed
      }

      const reader = response.body?.getReader();
      if (!reader) {
        const errorMsg = 'No readable body for stream';
        if (capture) {
          capture.captureError(errorMsg);
          capture.finalize();
        }
        throw new Error(errorMsg);
      }

      const decoder = new TextDecoder();
      let buffer = '';
      const isAwsEventStream = contentType.includes('application/octet-stream') || contentType.includes('application/vnd.amazon.eventstream');
      let awsEsBuffer = new Uint8Array(0);

      // Notify open-like state
      try { streamLog.init('🌊 Fetch SSE connection opened'); } catch (_) {}

      let totalBytes = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value && value.length) {
          totalBytes += value.length;
          // intentionally suppressed
        }
        if (isAwsEventStream && value) {
          // Accumulate AWS EventStream frames
          const merged = new Uint8Array(awsEsBuffer.length + value.length);
          merged.set(awsEsBuffer, 0);
          merged.set(value, awsEsBuffer.length);
          awsEsBuffer = merged;

          // Extract complete messages
          let offset = 0;
          const payloads: Uint8Array[] = [];
          while (awsEsBuffer.length - offset >= 12) {
            // totalLen(4) + headersLen(4) + preludeCrc(4)
            const view = new DataView(awsEsBuffer.buffer, awsEsBuffer.byteOffset + offset);
            const totalLen = view.getUint32(0, false);
            const headersLen = view.getUint32(4, false);
            if (totalLen <= 0 || totalLen > 10_000_000) break; // sanity
            if (awsEsBuffer.length - offset < totalLen) break; // wait for more data
            const payloadOffset = offset + 12 + headersLen;
            const payloadLen = totalLen - (12 + headersLen + 4);
            if (payloadLen > 0) {
              payloads.push(awsEsBuffer.subarray(payloadOffset, payloadOffset + payloadLen));
            }
            offset += totalLen;
          }
          // Keep remaining bytes
          awsEsBuffer = awsEsBuffer.subarray(offset);
          // Append decoded payloads into SSE text buffer
          // if (payloads.length > 0) {
          //   let bytes = 0;
          //   for (const p of payloads) bytes += p.length;
          // intentionally suppressed
          // }
          for (const p of payloads) {
            buffer += decoder.decode(p, { stream: true });
          }
        } else {
          buffer += decoder.decode(value, { stream: true });
        }

        // Process complete SSE events separated by double newlines (handle CRLF too)
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() || '';

        for (const rawEvent of events) {
          // Each event may have multiple lines; collect event: and data: lines
          const lines = rawEvent.split(/\r?\n/);
          let eventType = '';
          const dataLines: string[] = [];
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ') || line.startsWith('data:')) {
              // Per SSE spec, do not trim leading spaces from data lines
              const idx = line.indexOf('data:') + 5; // position after 'data:'
              const after = line.slice(idx);
              // If there is a single space after colon, preserve payload as-is; otherwise, after starts directly
              const payload = after.startsWith(' ') ? after.slice(1) : after;
              dataLines.push(payload);
            }
          }

          const dataStr = dataLines.join('\n');
          // intentionally suppressed
          try {
            const data = dataStr ? JSON.parse(dataStr) : {};

            // If server doesn't send an explicit SSE event type, infer from payload
            let inferredType = eventType;
            // Prefer top-level type from server payload when present (new format)
            const topType = typeof (data as any)?.type === 'string' ? String((data as any).type) : '';
            if (!inferredType || inferredType === 'message') {
              // Prefer explicit event field if present
              const payloadEvent = typeof (data as any)?.event === 'string' ? (data as any).event : '';
              switch (payloadEvent) {
                case 'start':
                  inferredType = 'agent_metadata';
                  break;
                case 'tool_call':
                  inferredType = 'agent_tool_call';
                  break;
                case 'tool_result':
                  inferredType = 'agent_tool_result';
                  break;
                case 'complete':
                  inferredType = 'agent_complete';
                  break;
                case 'done':
                  inferredType = 'agent_done';
                  break;
                case 'error':
                  inferredType = 'agent_error';
                  break;
              }

              // Next, prefer top-level type mapping of the new format
              if (!inferredType && topType) {
                switch (topType) {
                  case 'tool_call':
                    inferredType = 'agent_tool_call';
                    break;
                  case 'tool_result':
                    inferredType = 'agent_tool_result';
                    break;
                  case 'metadata':
                    inferredType = 'agent_metadata';
                    break;
                  case 'ai_content':
                    inferredType = 'agent_ai_content';
                    break;
                  case 'complete':
                    inferredType = 'agent_complete';
                    break;
                  case 'done':
                    inferredType = 'agent_done';
                    break;
                  case 'error':
                    inferredType = 'agent_error';
                    break;
                }
              }

              // Heuristics when neither explicit event nor top-level type present (legacy formats)
              if (!inferredType) {
                if (typeof (data as any)?.content === 'string' || typeof (data as any)?.deltaContent === 'string') {
                  inferredType = 'agent_ai_content';
                } else if ((data as any)?.toolName && (data as any)?.status === 'called') {
                  inferredType = 'agent_tool_call';
                } else if ((data as any)?.toolName && (data as any)?.status === 'complete') {
                  inferredType = 'agent_tool_result';
                } else if ((data as any)?.error) {
                  inferredType = 'agent_error';
                }
              }
            }

            if (!inferredType) {
              // No recognizable type; skip silently
              continue;
            }

            // Capture event data if enabled (with error boundary)
            if (capture && inferredType) {
              try {
                capture.captureEvent(inferredType, dataStr, data);
                
                // Capture AI content for accumulation
                if (inferredType === 'agent_ai_content') {
                  const content = data?.content || data?.deltaContent || '';
                  if (content) {
                    capture.captureContent(content);
                  }
                }
              } catch (captureError) {
                // Don't let capture errors break the stream
                try { streamLog.error('🚨 Stream capture error (non-blocking):', captureError); } catch (_) {}
              }
            }

            // Determine payload: new format nests payload under data
            const payload = (data && typeof data === 'object' && 'data' in (data as any))
              ? (data as any).data
              : data;

            // Always allow content log above; keep parsed-content log disabled to avoid double logs
            // Pass through phase/timestamp if present for downstream consumers
            const payloadWithMeta = (payload && typeof payload === 'object')
              ? { ...payload as any, phase: (data as any)?.phase, _timestamp: (data as any)?.timestamp }
              : payload;

            this.handleEvent(inferredType, payloadWithMeta);
          } catch (e) {
            const errorMsg = `Failed to parse ${eventType || 'event'}: ${e instanceof Error ? e.message : String(e)}`;
            try { streamLog.error(errorMsg); } catch (_) {}
            
            // Capture parse errors
            if (capture) {
              capture.captureError(errorMsg);
            }
            
            this.callbacks.onError?.(new Error(errorMsg));
          }
        }
      }

      // Stream ended - let orchestrator handle finalization with actual content
            this.callbacks.onDone?.();
    } catch (error) {
      if ((error as any)?.name === 'AbortError') {
        // Normal during disconnect - don't capture abort as error
        return;
      }
      
      const errorMsg = error instanceof Error ? error.message : String(error);
      try { streamLog.error('🚨 Fetch SSE connection error:', error); } catch (_) {}
      
      if (capture) {
        try {
          capture.captureError(`Connection error: ${errorMsg}`);
          capture.finalize();
        } catch (captureError) {
          try { streamLog.error('🚨 Stream capture finalization error (non-blocking):', captureError); } catch (_) {}
        }
      }
      
      this.callbacks.onError?.(error as Error);
    } finally {
      this.abortController = null;
    }
  }
}
